import React from 'react';

interface PoleManagementExportPreviewProps {
    tableData?: any[];
    exportType?: string;
    selectedExportFields?: string[];
}

const PoleManagementExportPreview = ({
    tableData = [],
    exportType = 'kmz',
    selectedExportFields = [],
}: PoleManagementExportPreviewProps) => {
    const totalPoles = tableData.reduce(
        (acc, job) => acc + (job.request_pole || 0),
        0,
    );
    const uniqueProjects = Array.from(
        new Set(tableData.map((job) => job.project)),
    ).filter(Boolean);
    const projectNames =
        uniqueProjects.length > 0
            ? uniqueProjects.length > 2
                ? `${uniqueProjects[0]}, ${uniqueProjects[1]}, +${uniqueProjects.length - 2}`
                : uniqueProjects.join(', ')
            : 'All Projects';

    let minDate = '';
    let maxDate = '';
    const dates = tableData
        .map((j) => new Date(j.request_date))
        .filter((d) => !isNaN(d.getTime()));
    if (dates.length > 0) {
        minDate = new Date(
            Math.min(...dates.map((d) => d.getTime())),
        ).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
        maxDate = new Date(
            Math.max(...dates.map((d) => d.getTime())),
        ).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    }
    const dateRangeStr =
        minDate && maxDate ? `${minDate} - ${maxDate}` : 'All Dates';

    return (
        <div>
            <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-center">
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    Export Preview{' '}
                    <span className="text-xs font-normal text-gray-500">
                        (Sample data that will be exported)
                    </span>
                </h2>
            </div>

            <div className="flex flex-col xl:flex-row gap-6">
                <div className="w-full xl:w-1/3">
                    <div className="flex gap-2 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                        <button
                            className={`px-3 py-1 text-xs rounded-md border ${exportType === 'kml' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-[#4F46E5] dark:text-indigo-400 font-semibold border-indigo-200 dark:border-indigo-800' : 'text-gray-500 dark:text-gray-400 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                            Google Earth (KML)
                        </button>
                        <button
                            className={`px-3 py-1 text-xs rounded-md border ${exportType === 'kmz' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-[#4F46E5] dark:text-indigo-400 font-semibold border-indigo-200 dark:border-indigo-800' : 'text-gray-500 dark:text-gray-400 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                            Google Earth (KMZ)
                        </button>
                        <button
                            className={`px-3 py-1 text-xs rounded-md border ${exportType === 'excel' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-[#4F46E5] dark:text-indigo-400 font-semibold border-indigo-200 dark:border-indigo-800' : 'text-gray-500 dark:text-gray-400 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                            Excel (.xlsx)
                        </button>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0 relative">
                            <div className="absolute inset-0 flex items-center justify-center text-gray-400 font-medium">
                                {exportType === 'excel' ? 'Spreadsheet' : 'Map'}
                            </div>
                            <div className="absolute top-1 left-1 text-[10px] text-white bg-black/50 px-1 rounded">
                                {exportType === 'excel'
                                    ? 'Excel'
                                    : 'Google Earth'}
                            </div>
                        </div>
                        <div className="text-sm flex flex-col gap-2">
                            <div className="font-semibold text-gray-700 dark:text-gray-300">
                                File contains:
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <span className="text-green-500">✓</span>{' '}
                                {totalPoles.toLocaleString()} poles
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <span className="text-green-500">✓</span>{' '}
                                Coordinates (Lat/Long)
                            </div>
                            
                            {exportType === 'excel' ? (
                                <>
                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                        <span className="text-green-500">✓</span> Pole Type
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                        <span className="text-green-500">✓</span> Status
                                    </div>
                                </>
                            ) : (
                                selectedExportFields.map((field) => (
                                    <div key={field} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                        <span className="text-green-500">✓</span> {field}
                                    </div>
                                ))
                            )}

                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <span className="text-green-500">✓</span>{' '}
                                Project: {projectNames}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <span className="text-green-500">✓</span> Date
                                Range: {dateRangeStr}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full xl:w-2/3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Sample Data (First 5 rows)
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-b border-t border-gray-200 dark:border-gray-700">
                                <tr>
                                    <th className="px-4 py-2 font-medium">
                                        Job No
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Pole Seq
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Pole Tag
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Pole Type
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Latitude
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Longitude
                                    </th>
                                    <th className="px-4 py-2 font-medium">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 dark:text-gray-300">
                                {tableData.slice(0, 5).map((job, index) => (
                                    <tr
                                        key={job.id || index}
                                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                                    >
                                        <td className="px-4 py-2">
                                            {job.number_job || '-'}
                                        </td>
                                        <td className="px-4 py-2">1</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2">-</td>
                                        <td className="px-4 py-2">
                                            {job.status || 'Completed'}
                                        </td>
                                    </tr>
                                ))}
                                {tableData.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-4 py-4 text-center text-gray-500"
                                        >
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PoleManagementExportPreview;
